
import {isBoolean, isEqual, isFunction, isNumber, isString, mapObject} from "../lib/underscore.js";

export function isNumberArray(arr) {
    return Array.isArray(arr) && arr.every(isNumber);
}

const intrinsicTesters = {
    boolean: isBoolean,
    number: isNumber,
    string: isString,
}

/**
 * @param {function} test ƒ(value, key) returns true if the value is valid, false (or exception) otherwise
 * @param {string} expectedType description for the expected type
 * @param {*} initialValue the initial value, which is considered valid
 * @returns {(function(*=, *=): void)|*} ƒ(key, value)
 */
function createValidator(test, expectedType, initialValue) {
    if (!isFunction(test)) {
        throw new Error();
    }
    return function validate(key, value) {
        if (!isEqual(value, initialValue) && !test(value, key)) {
            throw new Error(`expected ${expectedType} for '${key}'`);
        }
    };
}

function interpretSchemaRow([expectedType, initialValue]) {
    return isString(expectedType) ?
        createValidator(intrinsicTesters[expectedType], expectedType, initialValue) :
        createValidator(expectedType, "valid value", initialValue);
}

/**
 * Returns a function that validates a set of changes against a schema. Schema describes the expected structure of
 * an object, comprised of k,v pairs with the form:
 *
 *     {fieldName: [expectedType, initialValue]}
 *
 * where expectedType is either the name of an intrinsic ("string", "boolean", "number"), or ƒ(value, key) that
 * performs custom validation of the value (and the field it is being stored into).
 *
 * Example:
 * <pre>
 *     const validate = validatorFor({age: ["number", undefined]});
 *     validate({age: 100});        // valid, returns the input
 *     validate({age: "a"});        // not valid, throws an exception
 *     validate({age: undefined});  // valid, because matches initial value
 * </pre>
 *
 * @param schema
 * @returns {function(*=): *}
 */
export function validatorFor(schema) {
    const validators = mapObject(schema, interpretSchemaRow);

    return function validate(changes) {
        for (const [key, value] of Object.entries(changes)) {
            validators[key]?.(key, value);
        }
        return changes;
    };
}
